import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for managing wishlist state
 * Uses custom AJAX endpoints for Device Finder wishlist
 */

// PostHog tracking helper - always try to fire, PostHog queue will handle it
const trackEvent = (name, props = {}) => {
    if (typeof window === 'undefined' || !window.posthog) {
        return;
    }
    
    // PostHog has a queue system - if not loaded, it will queue the event
    // If loaded, it will fire immediately. So we can just call capture directly.
    try {
        window.posthog.capture(name, props);
    } catch (error) {
        // Silently fail
    }
};
const showWishlistSnackbar = () => {
    if (typeof document === 'undefined') return;

    let snackbar = document.getElementById('df-wishlist-snackbar');
    if (!snackbar) {
        snackbar = document.createElement('div');
        snackbar.id = 'df-wishlist-snackbar';
        snackbar.className = 'df-wishlist-snackbar';
        snackbar.innerHTML = '<span class="df-wishlist-snackbar__text">Προστέθηκε στη Λίστα Επιθυμιών.</span>' +
            '<a class="df-wishlist-snackbar__link" href="/wishlist/">Δες τη λίστα</a>';
        document.body.appendChild(snackbar);
    }

    snackbar.classList.add('df-wishlist-snackbar--show');

    if (snackbar._dfHideTimeout) {
        clearTimeout(snackbar._dfHideTimeout);
    }
    snackbar._dfHideTimeout = setTimeout(() => {
        snackbar.classList.remove('df-wishlist-snackbar--show');
    }, 3500);
};
const useWishlist = () => {
    const [wishlistItems, setWishlistItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toggleLoading, setToggleLoading] = useState({});
    const toggleLoadingRef = useRef({});
    const toggleQueueRef = useRef(Promise.resolve());
    const initialLoadDone = useRef(false);
    
    // Fetch wishlist items on mount
    useEffect(() => {
        if (initialLoadDone.current) return;
        initialLoadDone.current = true;
        
        fetchWishlist();
    }, []);
    
    // Fetch current wishlist
    const fetchWishlist = useCallback(async () => {
        try {
            setLoading(true);
            
            const formData = new FormData();
            formData.append('action', 'df_get_wishlist');
            
            const response = await fetch('/wp-admin/admin-ajax.php', {
                method: 'POST',
                body: formData,
                credentials: 'same-origin'
            });
            
            const data = await response.json();
            
            if (data.success && data.data?.items) {
                const items = data.data.items.map(id => parseInt(id, 10));
                setWishlistItems(items);
            } else {
                setWishlistItems([]);
            }
        } catch (error) {
            console.error('Error fetching wishlist:', error);
            setWishlistItems([]);
        } finally {
            setLoading(false);
        }
    }, []);
    
    // Check if product is in wishlist
    const isInWishlist = useCallback((productId) => {
        return wishlistItems.includes(parseInt(productId, 10));
    }, [wishlistItems]);
    
    // Toggle item in wishlist
    const toggleWishlistItem = useCallback((productId) => {
        const id = parseInt(productId, 10);
        
        // Prevent double-clicks
        if (toggleLoadingRef.current[id]) return;
        
        setToggleLoading(prev => {
            const next = { ...prev, [id]: true };
            toggleLoadingRef.current = next;
            return next;
        });
        
        const run = async () => {
            try {
                const formData = new FormData();
                formData.append('action', 'df_toggle_wishlist');
                formData.append('product_id', id);
                
                const response = await fetch('/wp-admin/admin-ajax.php', {
                    method: 'POST',
                    body: formData,
                    credentials: 'same-origin'
                });
                
                const data = await response.json();
                
                if (data.success) {
                    // Update local state based on action
                    if (data.data?.action === 'added') {
                        setWishlistItems(prev => [...prev, id]);
                        showWishlistSnackbar();
                        // PostHog tracking - wishlist added
                        trackEvent('device_finder_wishlist_added', {
                            product_id: id
                        });
                    } else if (data.data?.action === 'removed') {
                        setWishlistItems(prev => prev.filter(itemId => itemId !== id));
                        // PostHog tracking - wishlist removed
                        trackEvent('device_finder_wishlist_removed', {
                            product_id: id
                        });
                    }
                    
                    // Trigger custom event for other parts of the page
                    const event = new CustomEvent('df_wishlist_toggled', {
                        detail: {
                            item_id: id,
                            action: data.data?.action,
                            items: data.data?.items || []
                        }
                    });
                    document.body.dispatchEvent(event);
                    
                    return { success: true, action: data.data?.action };
                } else {
                    console.error('Toggle wishlist failed:', data);
                    return { success: false, error: data.data?.message || 'Unknown error' };
                }
            } catch (error) {
                console.error('Error toggling wishlist item:', error);
                return { success: false, error: error.message };
            } finally {
                setToggleLoading(prev => {
                    const next = { ...prev, [id]: false };
                    toggleLoadingRef.current = next;
                    return next;
                });
            }
        };

        toggleQueueRef.current = toggleQueueRef.current.then(run, run);
        return toggleQueueRef.current;
    }, []);
    
    // Check if a specific item is currently being toggled
    const isToggling = useCallback((productId) => {
        return !!toggleLoading[parseInt(productId, 10)];
    }, [toggleLoading]);
    
    return {
        wishlistItems,
        loading,
        isInWishlist,
        toggleWishlistItem,
        isToggling,
        refreshWishlist: fetchWishlist,
        wishlistCount: wishlistItems.length
    };
};

export default useWishlist;
